import { staticFile } from '@common/utils'
import RenderRichTextContent from '@components/common/render-rich-text-content'
import { QUIZZES_IMAGE_PATH } from '@lib/constants/quizzes'
import { QuizProblemType } from '@lib/types/quizzes'
import classNames from 'classnames'

type Props = {
  problem: QuizProblemType
  problemNumber: number
  areCorrectOptionsShown?: boolean
}

type ChoiceBulletProps = {
  currentChoiceId: number
  correctChoices: number[]
  submittedChoices: number[]
}

const ChoiceBullet = ({
  correctChoices,
  currentChoiceId,
  submittedChoices,
}: ChoiceBulletProps) => {
  const imagePath = `${QUIZZES_IMAGE_PATH}arena/result/`
  if (submittedChoices.includes(currentChoiceId)) {
    const isCorrect = correctChoices.includes(currentChoiceId)
    return (
      <img
        src={staticFile(
          `${imagePath}${
            isCorrect
              ? 'correct-choice-bullet-v1.png'
              : 'wrong-choice-bullet-v1.png'
          }`
        )}
        className="w-5"
      />
    )
  }
  return <img src={staticFile(`${imagePath}other-choice-bullet.png`)} />
}

type ChoicesProps = {
  choices: {
    id: number
    choice: string
  }[]
  correctChoices: number[]
  submittedChoices: number[]
  areCorrectOptionsShown?: boolean
}

const Choices = ({
  choices,
  correctChoices,
  submittedChoices,
  areCorrectOptionsShown = false,
}: ChoicesProps) => (
  <>
    {choices.map(({ id, choice }) => (
      <div key={id} className="flex items-start space-x-2">
        {!areCorrectOptionsShown && (
          <div className="flex items-center justify-center w-1/12 pt-1">
            <ChoiceBullet
              currentChoiceId={id}
              correctChoices={correctChoices}
              submittedChoices={submittedChoices}
            />
          </div>
        )}
        <RenderRichTextContent content={choice} className="w-5/6" />
      </div>
    ))}
  </>
)

const QuizSummaryProblemCard = ({
  problem: {
    choices,
    description,
    correctChoices,
    isCorrect,
    submittedChoices,
    solutionExplaination,
  },
  problemNumber,
  areCorrectOptionsShown = false,
}: Props) => (
  <div
    className={classNames(
      'leading-tight flex flex-col space-y-3 border-l-4 p-5 lg:p-8',
      {
        'bg-green-50 bg-opacity-75 border-green-200': isCorrect,
        'bg-red-50 border-red-200': !isCorrect,
      }
    )}
  >
    <div className="flex space-x-2">
      <div className="inline">{problemNumber}.</div>
      <div>
        <RenderRichTextContent content={description} />
      </div>
    </div>
    <div className="flex flex-col space-y-2">
      <Choices
        choices={choices}
        correctChoices={correctChoices}
        submittedChoices={submittedChoices}
      />
    </div>

    {areCorrectOptionsShown && (
      <>
        <div className="flex flex-col space-y-2">
          <h2 className="font-600">Correct answer</h2>
          <Choices
            choices={choices.filter((choice) =>
              correctChoices.includes(choice.id)
            )}
            correctChoices={correctChoices}
            submittedChoices={submittedChoices}
            areCorrectOptionsShown={areCorrectOptionsShown}
          />
        </div>
        <div className="flex flex-col space-y-2">
          <h2 className="font-600">Solution explanation</h2>
          <RenderRichTextContent content={solutionExplaination} />
        </div>
      </>
    )}
  </div>
)

export default QuizSummaryProblemCard
